import React, { useState } from 'react';
import { Col } from 'antd';
import { ImageCanvasPreview } from '../../../features/annotation';
import MediaPlaceholder from '../../../components/MediaPlaceholder';
import useImageFrame from '../../../hooks/useImageFrame';
import type { DamageGroup } from '../../../utils/damageGrouping';
import type { InspectionDamage } from '../inspectionTypes';
import type { InspectionMediaData, InspectionMediaType } from '../inspectionMediaTypes';

export type DamageItem = InspectionDamage & {
  id: string;
};

export interface DamageCardProps {
  group: DamageGroup<DamageItem>;
  viewName: string;
  isMobileInspection?: boolean;
  onItemClick?: (item?: InspectionMediaData, type?: InspectionMediaType) => void;
  colProps?: {
    xs?: number;
    sm?: number;
    md?: number;
    lg?: number;
  };
}

const DEFAULT_COL_PROPS = {
  xs: 12,
  sm: 8,
  md: 6,
  lg: 6,
};

const DamageCard: React.FC<DamageCardProps> = ({
  group,
  viewName,
  isMobileInspection = false,
  onItemClick,
  colProps = DEFAULT_COL_PROPS,
}) => {
  const [hasImageLoadError, setHasImageLoadError] = useState(false);
  const imageUrl = group.displaySrc;
  const { imageFrame, hasMeasuredFrame, handleImageLoad } = useImageFrame(imageUrl);

  if (!imageUrl) {
    console.error('No image URL found for damage group:', group);
    return null;
  }

  const handlePreviewClick = () => {
    onItemClick?.(
      {
        imageFileId: group.imageFileId,
        imageURI: imageUrl,
        title: viewName,
        damages: group.damages,
      },
      'image'
    );
  };

  const aspectClass = isMobileInspection ? 'aspect-[3/4]' : 'aspect-video';

  return (
    <Col {...colProps}>
      <div className={`relative overflow-hidden rounded bg-white group cursor-pointer ${aspectClass}`}>
        {!hasMeasuredFrame && !hasImageLoadError ? (
          <div className="relative h-full w-full">
            <MediaPlaceholder />
            <img
              src={imageUrl}
              alt="damage"
              onLoad={event => {
                handleImageLoad(event);
                setHasImageLoadError(false);
              }}
              onError={() => {
                console.error(`Failed to load image: ${imageUrl}`);
                setHasImageLoadError(true);
              }}
              className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-0"
            />
          </div>
        ) : hasImageLoadError ? (
          <div className="flex h-full w-full items-center justify-center bg-gray-100 px-4 text-center text-sm text-gray-500">
            Failed to load preview
          </div>
        ) : (
          <ImageCanvasPreview
            imageUrl={imageUrl}
            data={group}
            damages={group.damages}
            dimensions={imageFrame}
            onModalToggle={handlePreviewClick}
            pageInfo={12}
            user={{}}
          />
        )}
      </div>
    </Col>
  );
};

export default DamageCard;
